import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter.js";
import { parseKoreaDateTime } from "../../../../utilities/koreaTime";

dayjs.extend(isSameOrAfter);

export const RENTAL_CAR_TYPE_OTHER = "기타";

export const APPLICATION_KIND_RENTAL = "대여신청";
export const APPLICATION_KIND_OPERATION = "운행요청";

export const APPLICATION_KIND_OPTIONS = [
	APPLICATION_KIND_RENTAL,
	APPLICATION_KIND_OPERATION,
];

export const DRIVER_LICENSE_TYPE_OPTIONS = [
	"1종 대형",
	"1종 보통",
	"2종 보통",
];

export const MAX_RENTAL_REQUESTED_COUNT = 10;

export const API_RENTAL_CAR_TYPES = "/utils/rental/car/types/";
export const API_RENTAL_APPLY_TYPES = "/utils/rental/car/apply-types/";

export const RENTAL_CAR_APPLICATION_STATUS = {
	APPLIED: "신청",
	APPROVED: "승인",
	REJECTED: "거절",
};

export const SCHEDULE_TYPE_RENTAL = "대여";
export const SCHEDULE_TYPE_OPERATION = "운행";

export const RENTAL_APPLY_CHECK_LABELS = [
	"차량 이용 확정 안내문자를 꼭 확인해주세요.",
	"키를 수령하실 때 서약서를 작성해주세요.",
];

export const RENTAL_APPLY_NOTICE =
	"신청순으로 처리되므로 먼저 신청하신 분이 있을 경우 반려될 수 있습니다.";

/** 지난 날짜는 신청할 수 없음 */
export const isDateDisabledForApply = (date) =>
	!!date && date.isBefore(dayjs(), "day");

/** 신청 목록·요약 표시용 차량 종류 라벨 */
export const formatCarTypeLabel = (application) => {
	if (!application) {
		return "-";
	}
	let label = application.car_type_name;
	if (application.car_type_name === RENTAL_CAR_TYPE_OTHER) {
		const custom = (application.custom_car_type || "").trim();
		label = custom ? `기타 - ${custom}` : "기타";
	}
	const count = Number(application.requested_count) || 1;
	if (count > 1) {
		return `${label} ${count}대`;
	}
	return label || "-";
};

export const matchRequestedApplyTypeId = (application, applyTypes) => {
	if (!application) {
		return undefined;
	}
	if (application.requested_apply_type != null) {
		return application.requested_apply_type;
	}
	return applyTypes.find((type) => type.name === application.car_type_name)
		?.id;
};

export const getAssignableCarTypes = (application) =>
	application?.matched_car_types || [];

/** 달력 차량 표시용: 괄호(및 전각 괄호) 안 문자열을 숨긴다. */
const stripParentheticalText = (text) => {
	const value = text == null ? "" : String(text);
	return value
		.replace(/\s*[(\uFF08][^)\uFF09]*[)\uFF09]/g, "")
		.replace(/\s+/g, " ")
		.trim();
};

export const getApplyScheduleCarLabel = (schedule) => {
	const raw = schedule?.custom_car_number?.trim()
		? schedule.custom_car_number.trim()
		: schedule?.car_type_name || "-";
	return stripParentheticalText(raw) || raw;
};

export const getApplyScheduleColor = (schedule) =>
	schedule?.car_type_color || "#2457e6";

export const getScheduleDayStart = (schedule) => {
	if (schedule.start_at) {
		return parseKoreaDateTime(schedule.start_at).startOf("day");
	}
	return dayjs(schedule.start_date).startOf("day");
};

export const getScheduleDayEnd = (schedule) => {
	if (schedule.end_at) {
		return parseKoreaDateTime(schedule.end_at).startOf("day");
	}
	return dayjs(schedule.end_date).startOf("day");
};

const getScheduleSortMs = (schedule) => {
	if (schedule.start_at) {
		return parseKoreaDateTime(schedule.start_at).valueOf();
	}
	return dayjs(schedule.start_date).startOf("day").valueOf();
};

const isAllDaySchedule = (schedule) => {
	if (!schedule?.start_at || !schedule?.end_at) {
		return false;
	}
	const start = parseKoreaDateTime(schedule.start_at);
	const end = parseKoreaDateTime(schedule.end_at);
	if (!start.isValid() || !end.isValid()) {
		return false;
	}
	if (!end.isSameOrAfter(start, "day")) {
		return false;
	}
	const startOk =
		start.hour() === 0 &&
		start.minute() === 0 &&
		start.second() === 0;
	const endOk =
		end.hour() === 23 &&
		end.minute() === 59 &&
		(end.second() === 58 || end.second() === 59);
	return startOk && endOk;
};

export const formatScheduleRangeLabel = (schedule) => {
	if (schedule.start_at && schedule.end_at) {
		if (isAllDaySchedule(schedule)) {
			const start = parseKoreaDateTime(schedule.start_at);
			const end = parseKoreaDateTime(schedule.end_at);
			if (start.isSame(end, "day")) {
				return `${start.format("YYYY-MM-DD")} 종일`;
			}
			return `${start.format("YYYY-MM-DD")} ~ ${end.format("YYYY-MM-DD")} 종일`;
		}
		return `${parseKoreaDateTime(schedule.start_at).format("YYYY-MM-DD HH:mm")} ~ ${parseKoreaDateTime(schedule.end_at).format("YYYY-MM-DD HH:mm")}`;
	}
	return `${dayjs(schedule.start_date).format("YYYY-MM-DD")} ~ ${dayjs(schedule.end_date).format("YYYY-MM-DD")}`;
};

export const formatCalendarBarTimeParen = (schedule) => {
	if (schedule.start_at && schedule.end_at) {
		const start = parseKoreaDateTime(schedule.start_at);
		const end = parseKoreaDateTime(schedule.end_at);
		if (!start.isValid() || !end.isValid()) {
			return "";
		}
		if (isAllDaySchedule(schedule)) {
			return "(종일)";
		}
		if (start.isSame(end, "day")) {
			return `(${start.format("HH:mm")} ~ ${end.format("HH:mm")})`;
		}
		return `(${start.format("M/D HH:mm")} ~ ${end.format("M/D HH:mm")})`;
	}
	const startDate = dayjs(schedule.start_date);
	const endDate = dayjs(schedule.end_date);
	if (!startDate.isValid() || !endDate.isValid()) {
		return "";
	}
	if (startDate.isSame(endDate, "day")) {
		return `(${startDate.format("M/D")})`;
	}
	return `(${startDate.format("M/D")}~${endDate.format("M/D")})`;
};

export const truncateCellTagText = (text, maxLen = 4) => {
	const value = text == null ? "" : String(text);
	if (value.length <= maxLen) {
		return value;
	}
	return value.slice(0, maxLen);
};

export const attachApplyCalendarMeta = (schedule) => {
	const dayStart = getScheduleDayStart(schedule);
	const dayEnd = getScheduleDayEnd(schedule);
	const endForSort =
		schedule.end_at != null && schedule.end_at !== ""
			? parseKoreaDateTime(schedule.end_at)
			: dayjs(schedule.end_date).endOf("day");
	return {
		...schedule,
		_calendarKey: `rental-${schedule.id}`,
		_sortMs: getScheduleSortMs(schedule),
		_endSortMs: endForSort.isValid()
			? endForSort.valueOf()
			: dayEnd.valueOf(),
		_dayStart: dayStart,
		_dayEnd: dayEnd,
		_dayStartKey: dayStart.format("YYYY-MM-DD"),
		_dayEndKey: dayEnd.format("YYYY-MM-DD"),
	};
};

export const compareCalendarItems = (a, b) => {
	const sortDiff = a._sortMs - b._sortMs;
	if (sortDiff !== 0) {
		return sortDiff;
	}
	const endDiff = a._endSortMs - b._endSortMs;
	if (endDiff !== 0) {
		return endDiff;
	}
	return String(a._calendarKey).localeCompare(String(b._calendarKey));
};
